import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Avatar, Empty, Space, Table, Tag } from "antd";
import {
    HolderOutlined,
    TrophyOutlined,
} from "@ant-design/icons";
import {
    DndContext,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
    SortableContext,
    arrayMove,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import { IAward } from "../../../../../../../interfaces";
import { ThemeContext } from "../../../../../../../contexts/ThemeContext";
import AwardsTableRowActions from "./AwardsTableRowActions";
import { formatCurrency } from "../../../../../../../utils/Masks";
import AwardsListTableFooter from "./AwardsListTableFooter";
import { IAwardsOrderObject } from "../../../../../../../services/http/services/AwardsService";
import AwardsListTableRow from "./AwardsListTableRow";
import { awardImagesUrl } from "../../../../../../../services/urls";

interface IProps {
    awards: Array<IAward>;
    onClickEditAward: (award: IAward) => void;
    onClickDeleteAward: (award: IAward) => void;
    onUpdateAwardsOrder: (
        awardsOrder: Array<IAwardsOrderObject>
    ) => Promise<void>;
    onAwardsOrderChanged: (isEqual: boolean) => void;
    giveawayEnded: boolean;
    hasSocialInstitutionModule?: boolean;
}

interface ITableData extends IAward { }

export default function AwardsList({
    awards,
    onClickEditAward,
    onClickDeleteAward,
    onUpdateAwardsOrder,
    onAwardsOrderChanged,
    giveawayEnded,
    hasSocialInstitutionModule = false,
}: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    const [isUpdatingOrder, setIsUpdatingOrder] = useState<boolean>(false);
    const [tableData, setTableData] = useState<Array<ITableData>>([]);
    const [orderedTableData, setOrderedTableData] = useState<Array<ITableData>>(
        []
    );

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 1,
            },
        })
    );

    const onDragEnd = ({ active, over }: any) => {
        if (active.id !== over?.id) {
            setOrderedTableData((prev) => {
                const activeIndex = prev.findIndex((i) => i.id === active.id);
                const overIndex = prev.findIndex((i) => i.id === over?.id);
                return arrayMove(prev, activeIndex, overIndex);
            });
        }
    };

    const isEqual = orderedTableData.every((row, index) => {
        const initialIndex = tableData.findIndex((r) => r.id === row.id);
        return initialIndex === index;
    });

    const handleClickSaveOrder = useCallback(async () => {
        setIsUpdatingOrder(true);

        await onUpdateAwardsOrder(
            orderedTableData.map((data, index) => ({
                id: data.id,
                order: index + 1,
            }))
        );

        setIsUpdatingOrder(false);
    }, [onUpdateAwardsOrder, orderedTableData]);

    const handleCancelOrder = useCallback(() => {
        setOrderedTableData(tableData);
    }, [tableData]);

    const totalAwardsValue = useMemo(() => {
        return awards.reduce((acc, curr) => acc + (Number(curr.value) || 0), 0);
    }, [awards]);

    const totalSocialInstitutionValue = useMemo(() => {
        return awards.reduce((acc, curr) => acc + (Number(curr.social_institution_value) || 0), 0);
    }, [awards]);

    const getMedalBadge = (index: number) => {
        if (index === 0) {
            return (
                <Tag
                    color="gold"
                    style={{
                        fontWeight: 700,
                        borderRadius: 6,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                    }}
                >
                    🥇 1º Prêmio
                </Tag>
            );
        }
        if (index === 1) {
            return (
                <Tag
                    color="default"
                    style={{
                        fontWeight: 700,
                        borderRadius: 6,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                    }}
                >
                    🥈 2º Prêmio
                </Tag>
            );
        }
        if (index === 2) {
            return (
                <Tag
                    color="orange"
                    style={{
                        fontWeight: 700,
                        borderRadius: 6,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                    }}
                >
                    🥉 3º Prêmio
                </Tag>
            );
        }
        return (
            <Tag
                color="blue"
                style={{
                    fontWeight: 600,
                    borderRadius: 6,
                }}
            >
                {index + 1}º Prêmio
            </Tag>
        );
    };

    const columns = [
        {
            title: "Posição",
            dataIndex: "order",
            key: "order",
            width: 130,
            render: (_: any, __: any, index: number) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {!giveawayEnded && (
                        <HolderOutlined
                            style={{
                                color: isDark ? '#595959' : '#8c8c8c',
                                cursor: 'grab',
                                fontSize: 13,
                            }}
                        />
                    )}
                    {getMedalBadge(index)}
                </div>
            ),
        },
        {
            title: "Descrição do Prêmio",
            dataIndex: "description",
            key: "description",
            render: (text: string, record: ITableData) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {record.images && record.images.length > 0 ? (
                        <Avatar
                            shape="square"
                            size={36}
                            src={`${awardImagesUrl}/${record.images[0].img}`}
                            style={{ borderRadius: 6, flexShrink: 0 }}
                        />
                    ) : (
                        <Avatar
                            shape="square"
                            size={36}
                            icon={<TrophyOutlined />}
                            style={{
                                borderRadius: 6,
                                background: isDark ? '#262626' : '#f0f2f5',
                                color: '#1677ff',
                                flexShrink: 0,
                            }}
                        />
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: 600, color: isDark ? '#f0f0f0' : '#1e293b' }}>
                            {text}
                        </span>
                    </div>
                </div>
            ),
        },
        {
            title: "Valor Estimado",
            dataIndex: "value",
            key: "value",
            width: hasSocialInstitutionModule ? 180 : 140,
            render: (_: any, record: ITableData) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontWeight: 700, color: '#52c41a', fontSize: 13 }}>
                        R$ {formatCurrency(record.value.toString())}
                    </span>
                    {hasSocialInstitutionModule && (
                        <span style={{ fontSize: 11, color: isDark ? '#94a3b8' : '#64748b' }}>
                            Inst. Social:{' '}
                            <strong style={{ color: '#1677ff' }}>
                                R$ {formatCurrency((record.social_institution_value ?? 0).toString())}
                            </strong>
                        </span>
                    )}
                </div>
            ),
        },
        {
            title: "Ações",
            dataIndex: "actions",
            key: "actions",
            width: 90,
            align: 'center' as const,
            render: (_: any, award: ITableData) => (
                <AwardsTableRowActions
                    award={award}
                    onClickEditAward={onClickEditAward}
                    onClickDeleteAward={() => onClickDeleteAward(award)}
                    disableDelete={!isEqual || giveawayEnded}
                />
            ),
        },
    ];

    useEffect(() => {
        onAwardsOrderChanged(isEqual);
    }, [isEqual, onAwardsOrderChanged]);

    useEffect(() => {
        setTableData(awards);
        setOrderedTableData([...awards].sort((a, b) => a.order - b.order));
    }, [awards]);

    const sectionTitleStyle: React.CSSProperties = {
        fontSize: 13,
        fontWeight: 600,
        color: isDark ? '#94a3b8' : '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    };

    const cardStyle = {
        background: isDark ? '#141414' : '#ffffff',
        borderRadius: 10,
        border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
        boxShadow: isDark
            ? '0 4px 20px rgba(0, 0, 0, 0.35)'
            : '0 2px 10px rgba(0, 0, 0, 0.03)',
        padding: '24px',
        height: '100%',
    };

    return (
        <div style={cardStyle}>
            {/* Header do Card */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 16,
                    flexWrap: 'wrap',
                    gap: 10,
                }}
            >
                <div style={sectionTitleStyle}>
                    <TrophyOutlined style={{ color: '#faad14' }} />
                    <span>Prêmios Cadastrados</span>
                </div>

                <Space size={6} wrap>
                    <Tag color="purple" style={{ fontWeight: 600, borderRadius: 6 }}>
                        {awards.length} {awards.length === 1 ? 'prêmio' : 'prêmios'}
                    </Tag>
                    {totalAwardsValue > 0 && (
                        <Tag color="green" style={{ fontWeight: 600, borderRadius: 6 }}>
                            Total: R$ {formatCurrency(totalAwardsValue.toString())}
                        </Tag>
                    )}
                    {hasSocialInstitutionModule && totalSocialInstitutionValue > 0 && (
                        <Tag color="blue" style={{ fontWeight: 600, borderRadius: 6 }}>
                            Inst. Social: R$ {formatCurrency(totalSocialInstitutionValue.toString())}
                        </Tag>
                    )}
                </Space>
            </div>

            <DndContext
                sensors={sensors}
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={onDragEnd}
            >
                <SortableContext
                    items={orderedTableData.map((i) => i.id)}
                    strategy={verticalListSortingStrategy}
                    disabled={giveawayEnded}
                >
                    <Table
                        size="middle"
                        className={`modern-data-table ${isDark ? 'modern-data-table-dark' : ''}`}
                        components={{
                            body: {
                                row: AwardsListTableRow,
                            },
                        }}
                        columns={columns}
                        dataSource={orderedTableData}
                        rowKey={(award) => award.id}
                        locale={{
                            emptyText: (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={
                                        <div style={{ padding: '12px 0' }}>
                                            <div style={{ fontSize: 14, fontWeight: 600, color: isDark ? '#ccc' : '#475569' }}>
                                                Nenhum prêmio cadastrado
                                            </div>
                                            <div style={{ fontSize: 12, color: isDark ? '#8c8c8c' : '#94a3b8', marginTop: 4 }}>
                                                Preencha o formulário ao lado para cadastrar o primeiro prêmio deste sorteio.
                                            </div>
                                        </div>
                                    }
                                />
                            ),
                        }}
                        footer={() => (
                            <AwardsListTableFooter
                                show={!isEqual}
                                onClickSaveOrder={handleClickSaveOrder}
                                onClickCancel={handleCancelOrder}
                                isSubmiting={isUpdatingOrder}
                            />
                        )}
                        pagination={false}
                    />
                </SortableContext>
            </DndContext>
        </div>
    );
}

